import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
} from '@mui/material';
import { Close as CloseIcon, AddLocationAltOutlined as AddLocationIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';
import { DecimalNumberInput } from '../../components/ui';
import { SafetyStockService } from '../../api/services/SafetyStockService';
import type { Location } from '../../api/models/Location';
import { dialogZoomPaperSx } from '../../components/ui/DialogZoomTransition';
import { CloseConfirmationModal } from '../../components/modals/confirmation';

export interface AddSafetyStockProduct {
  id: number;
  name: string;
  code: string;
}

export interface AddSafetyStockLocationModalProps {
  open: boolean;
  onClose: () => void;
  product: AddSafetyStockProduct | null;
  locations: Location[];
  onSuccess: () => void;
}

function locationTypeLabel(type: Location['type'] | undefined): string {
  if (type === 'WAREHOUSE') return 'Almacén';
  if (type === 'BRANCH') return 'Sucursal';
  return type ?? '';
}

const AddSafetyStockLocationModal: React.FC<AddSafetyStockLocationModalProps> = ({
  open,
  onClose,
  product,
  locations,
  onSuccess,
}) => {
  const [sessionProduct, setSessionProduct] = useState<AddSafetyStockProduct | null>(null);
  const [locationId, setLocationId] = useState('');
  const [safetyStock, setSafetyStock] = useState('');
  const [assignedLocationIds, setAssignedLocationIds] = useState<number[]>([]);
  const [assignedLoadFailed, setAssignedLoadFailed] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  const resetForm = useCallback(() => {
    setLocationId('');
    setSafetyStock('');
    setError(null);
    setConfirmed(false);
    setAssignedLoadFailed(false);
    setAssignedLocationIds([]);
    setShowCloseConfirmation(false);
  }, []);

  // Snapshot product for the open session so parent selection changes cannot unmount mid-dialog.
  useEffect(() => {
    if (!open) return;
    if (product == null) return;
    setSessionProduct(product);
  }, [open, product]);

  const availableLocations = useMemo(() => {
    // Fail closed: never expose locations when the assigned set failed to load.
    if (assignedLoadFailed) return [];
    const assigned = new Set(assignedLocationIds);
    return locations.filter((loc) => !assigned.has(loc.id));
  }, [locations, assignedLocationIds, assignedLoadFailed]);

  useEffect(() => {
    if (!open || product == null) return;

    resetForm();
    setLoadingLocations(true);
    let cancelled = false;

    SafetyStockService.productLocationSafetyStockControllerGetAssignedLocationIds(product.id)
      .then((res) => {
        if (cancelled) return;
        setAssignedLocationIds(res?.locationIds ?? []);
        setAssignedLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setAssignedLocationIds([]);
        setAssignedLoadFailed(true);
        setError('No se pudieron cargar las ubicaciones disponibles.');
      })
      .finally(() => {
        if (!cancelled) setLoadingLocations(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, product, resetForm]);

  const hasChanges = useMemo(
    () => locationId !== '' || safetyStock.trim() !== '' || confirmed,
    [confirmed, locationId, safetyStock],
  );

  const doClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleClose = useCallback(() => {
    if (loading) return;
    if (hasChanges) {
      setShowCloseConfirmation(true);
      return;
    }
    doClose();
  }, [doClose, hasChanges, loading]);

  const handleConfirmClose = useCallback(() => {
    doClose();
  }, [doClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  const handleSave = async () => {
    if (sessionProduct == null) return;
    if (assignedLoadFailed) {
      setError('No se pudieron cargar las ubicaciones disponibles.');
      return;
    }
    if (!locationId) {
      setError('Seleccione una ubicación.');
      return;
    }
    if (safetyStock.trim() === '') {
      setError('Ingrese el stock de seguridad.');
      return;
    }
    const value = parseInt(safetyStock, 10);
    if (Number.isNaN(value) || value < 0) {
      setError('El valor debe ser un número entero mayor o igual a 0.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await SafetyStockService.productLocationSafetyStockControllerUpsert(
        sessionProduct.id,
        Number(locationId),
        { safetyStock: value },
      );
      onSuccess();
      doClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Error al agregar el stock de seguridad para esa ubicación.',
      );
    } finally {
      setLoading(false);
    }
  };

  const displayProduct = sessionProduct;
  if (displayProduct == null) return null;

  const hasAvailable = !assignedLoadFailed && availableLocations.length > 0;
  const showForm = !loadingLocations && !assignedLoadFailed && hasAvailable;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="add-safety-stock-location-title"
        PaperProps={{
          sx: dialogZoomPaperSx({
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0 20px 60px rgba(2, 6, 23, 0.18)',
            margin: 'auto',
            width: '100%',
            maxWidth: { xs: 'min(600px, calc(100% - 32px))', sm: '600px' },
            bgcolor: 'background.paper',
          }),
        }}
      >
        <DialogTitle
          id="add-safety-stock-location-title"
          sx={(theme) => ({
            padding: '14px 20px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600,
            fontSize: '1rem',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              minWidth: 0,
              justifyContent: 'flex-start',
            }}
          >
            <AddLocationIcon sx={{ fontSize: 22, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#ffffff',
                fontSize: '1rem',
                overflow: 'visible',
                textOverflow: 'clip',
              }}
            >
              Agregar mínimo a ubicación
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            aria-label="Cerrar"
            sx={{
              color: '#ffffff',
              '&:hover': { color: alpha('#fff', 0.85) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={(theme) => ({
            padding: '48px 28px 32px',
            overflowY: 'auto',
            overflowX: 'visible',
            minHeight: 120,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {error != null && error !== '' && (
            <Alert
              severity="error"
              sx={(theme) => ({
                mb: 2,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor:
                  theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.15) : '#fee2e2',
                color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
                border: `1px solid ${
                  theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.35) : '#fecaca'
                }`,
                '& .MuiAlert-icon': {
                  color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
                },
              })}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mt: 1,
              width: '100%',
              minWidth: 0,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Producto
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {displayProduct.name} ({displayProduct.code})
              </Typography>
            </Box>

            {loadingLocations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : assignedLoadFailed ? (
              <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                No se pudo determinar qué ubicaciones están libres. Cierre e intente de nuevo.
              </Alert>
            ) : !hasAvailable ? (
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                Esta pieza ya tiene asignación en todas las ubicaciones.
              </Alert>
            ) : (
              <>
                <TextField
                  select
                  fullWidth
                  label="Ubicación *"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Seleccione sucursal o almacén
                  </MenuItem>
                  {availableLocations.map((loc) => {
                    const typeLabel = locationTypeLabel(loc.type);
                    return (
                      <MenuItem key={loc.id} value={String(loc.id)}>
                        {loc.name}
                        {typeLabel !== '' ? ` (${typeLabel})` : ''}
                      </MenuItem>
                    );
                  })}
                </TextField>

                <DecimalNumberInput
                  fullWidth
                  variant="outlined"
                  size="medium"
                  label="Stock de seguridad *"
                  value={safetyStock}
                  onChange={(val) => setSafetyStock(val != null ? String(Math.trunc(val)) : '')}
                  placeholder="0"
                  decimalPlaces={0}
                  min={0}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      disabled={loading}
                      sx={{
                        color: colors.success.main,
                        '&.Mui-checked': { color: colors.success.main },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                    >
                      Confirmar alta de mínimo en esta ubicación
                    </Typography>
                  }
                  sx={{ marginLeft: 0 }}
                />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={(theme) => ({
            padding: '16px 24px',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.grey[50],
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: 1.5,
            rowGap: 1.5,
            overflow: 'visible',
          })}
        >
          <Button
            variant="tonal"
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: '120px',
              padding: '9px 18px',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            {showForm ? 'Cancelar' : 'Cerrar'}
          </Button>
          {showForm && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !confirmed}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              sx={(theme) => ({
                minWidth: '120px',
                padding: '9px 18px',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
                color: '#ffffff',
                boxShadow: 'none',
                '&:hover': {
                  background: 'linear-gradient(to right, #0891b2, #059669)',
                  boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
                },
                '&:disabled': {
                  background: colors.grey[400],
                  color: '#ffffff',
                  opacity: theme.palette.mode === 'dark' ? 0.5 : 0.6,
                  cursor: 'not-allowed',
                  boxShadow: 'none',
                },
              })}
            >
              {loading ? 'Guardando...' : 'Agregar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default AddSafetyStockLocationModal;
